import { dataApi } from "apis/dataV1";
import { Permission } from "apis/dataV2/permissions";
import { WidgetKey } from "config/features";
import { User } from "slices/authSlice";
import { telemetry } from "telemetry";
import { base64UrlEncode, generateRandomString, sha256 } from "utils/misc";

export class AuthError extends Error {
    constructor(
        message: string,
        /** Discriminates the failure origin so callers can classify without re-inspecting statusCode. */
        public readonly reason: "network" | "http" | "parse",
        /** HTTP status code when the server replied; only set when reason is "http". */
        public readonly statusCode?: number,
    ) {
        super(message);
        this.name = "AuthError";
    }
}

/**
 * Fetches the current user and throws a typed {@link AuthError} on every failure path.
 *
 * Failure modes:
 *   - Network/CORS error              → throws AuthError(reason="network")
 *   - HTTP non-ok response (4xx/5xx)  → throws AuthError(reason="http", statusCode)
 *   - HTTP 200 but unparseable JSON   → throws AuthError(reason="parse")
 *
 * Prefer this over {@link getUser} when the caller needs to distinguish failure
 * kinds (e.g. for telemetry / error classification in {@link useInitLibAuth}).
 */
export async function fetchUser(accessToken: string): Promise<User> {
    let response: Response;
    try {
        response = await fetch(dataApi.serviceUrl + "/api/user", {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
    } catch {
        // Network-level failure (DNS, TCP, CORS preflight, etc.)
        throw new AuthError("Network error while fetching user", "network");
    }

    if (!response.ok) {
        throw new AuthError(`User request failed with status ${response.status}`, "http", response.status);
    }

    let data: unknown;
    try {
        data = await response.json();
    } catch {
        throw new AuthError("Failed to parse user response as JSON", "parse");
    }

    return data as User;
}

/**
 * Non-throwing convenience wrapper around {@link fetchUser}.
 *
 * Returns the {@link User} on success, or `undefined` on any failure.
 * Use this at call sites that only need "user or nothing" and have no use
 * for the failure reason (e.g. the OAuth redirect / iframe-auth flows in
 * {@link App} where a failed lookup simply means the user is not logged in).
 */
export async function getUser(accessToken: string): Promise<User | undefined> {
    return fetchUser(accessToken).catch(() => undefined);
}

export async function generateCodeChallenge(): Promise<[verifier: string, challenge: string]> {
    const verifier = generateRandomString();
    const hashed = await sha256(verifier);
    return [verifier, base64UrlEncode(hashed)];
}

type OAuthState = {
    service?: WidgetKey | "self";
    sceneId?: string;
    query?: string;
    space?: string;
    localBookmarkId?: string;
};

export function getOAuthState(): OAuthState | undefined {
    const stateKey = new URLSearchParams(window.location.search).get("state");

    if (!stateKey) {
        return;
    }

    const state = sessionStorage.getItem(stateKey);

    if (!state) {
        console.warn("OAuth state mismatch.");
        return;
    }

    try {
        return JSON.parse(state);
    } catch (e) {
        console.warn(e);
        telemetry.trackCaughtException({ source: "auth.utils.getOAuthState", category: "auth", error: e });
        return;
    }
}

export function createOAuthStateString(state: OAuthState): string {
    const id = window.crypto.randomUUID();
    const stateStr = JSON.stringify(state);
    sessionStorage.setItem(id, stateStr);

    return id;
}

export function checkPermission(permissions: Set<Permission>, permission: Permission) {
    // permissions are expected to have both explicit and implicit permissions,
    // so we don't have to check for parent permissions
    return permissions.has(permission);
}
